import { Link } from "react-router-dom";
import React, { useState } from "react";
import { AlertCircle, CheckCircle2, Info, Layers, Loader2, Medal, Pencil, Plus, Trash2, Users } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { BulkBar, RowBox, SelectAllBox, useTableSelection } from "../../components/TableSelection";
import { Button, EmptyState, Loading, Modal, SectionCard, StatCard } from "../../components/ui";
import { apiDelete, apiPost, apiPut, useApi } from "../../api/client";
import { RifleClass, RifleGrade, UnknownGrade } from "../../data/types";

/*
 * The rifle class register.
 *
 * This page used to be a read-only view of whatever classes happened to appear
 * in an upload, which meant a class could be created by a typo and nothing said
 * so. Classes are now records: each one defines how it scores, an upload is
 * matched against the list, and anything unrecognised is reported on the import
 * instead of quietly becoming a new class with its own leaderboard.
 *
 * Scoring is the part that matters. Target Rifle is 5 down to 1 with a V centre
 * and F-Class is 6 down to 1 with an X, so a class with different rings — an
 * ISSF 10-ring target, say — is a matter of entering the right numbers here.
 */
export default function AdminClasses() {
  const { data: classes, loading, reload } = useApi<RifleClass[]>("/classes", []);
  const [editing, setEditing] = useState<RifleClass | null>(null);
  const [pending, setPending] = useState<RifleClass[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);
  const selection = useTableSelection(classes.map((c) => c.code));

  /*
   * Grades are their own register, fetched alongside the classes. `unknown`
   * lists values in use that are not on it — a typo, or a discipline's grade
   * nobody has added yet.
   */
  const { data: grades, reload: reloadGrades } = useApi<RifleGrade[]>("/grades", []);
  const { data: unknownGrades, reload: reloadUnknown } = useApi<UnknownGrade[]>("/grades/unknown", []);
  const [editingGrade, setEditingGrade] = useState<RifleGrade | null>(null);
  const [pendingGrade, setPendingGrade] = useState<RifleGrade | null>(null);

  const refreshGrades = () => {
    reloadGrades();
    reloadUnknown();
  };

  const saveGrade = async (g: RifleGrade) => {
    setBusy(true);
    try {
      const body = { code: g.code, name: g.name, n: g.n, aliases: g.aliases, description: g.description };
      const r = g.code && grades.some((x) => x.code === g.code)
        ? await apiPut<{ message: string }>(`/admin/grades/${encodeURIComponent(g.code)}`, body)
        : await apiPost<{ message: string }>("/admin/grades", body);
      setEditingGrade(null);
      refreshGrades();
      setNotice({ tone: "ok", text: r.message || "Grade saved." });
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    }
    setBusy(false);
  };

  const removeGrade = async () => {
    if (!pendingGrade) return;
    setBusy(true);
    try {
      const r = await apiDelete<{ message: string }>(`/admin/grades/${encodeURIComponent(pendingGrade.code)}`);
      setNotice({ tone: "ok", text: r.message || "Grade removed." });
      refreshGrades();
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    }
    setPendingGrade(null);
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    const failed: string[] = [];
    for (const c of pending) {
      try {
        await apiDelete(`/admin/classes/${encodeURIComponent(c.code)}`);
      } catch (e) {
        failed.push((e as Error).message);
      }
    }
    setBusy(false);
    setPending([]);
    selection.clear();
    reload();
    setNotice(
      failed.length
        ? { tone: "bad", text: failed[0] }
        : { tone: "ok", text: `${pending.length} class${pending.length === 1 ? "" : "es"} removed.` }
    );
  };

  return (
    <div>
      <AdminTopbar
        title="Classes & Grades"
        subtitle="The rifle classes uploads are matched against, and how each one scores."
        actions={
          <Button onClick={() => setEditing({ code: "", name: "", maxPerShot: 5, centreLabel: "V", aliases: [], description: "" } as RifleClass)}>
            <Plus size={16} /> Add Class
          </Button>
        }
      />

      {notice && (
        <div
          className={`mb-3 flex items-start gap-2.5 rounded-xl border px-4 py-3 text-[13px] ${
            notice.tone === "ok" ? "border-success/30 bg-success-soft text-success" : "border-danger/30 bg-danger-soft text-danger"
          }`}
        >
          {notice.tone === "ok" ? <CheckCircle2 size={16} className="mt-px shrink-0" /> : <AlertCircle size={16} className="mt-px shrink-0" />}
          <span className="min-w-0 font-semibold">{notice.text}</span>
          <button className="ml-auto shrink-0 font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={<Layers size={20} />} value={classes.length} label="Rifle Classes" sub="In the register" />
        <StatCard icon={<Users size={20} />} value={classes.reduce((a, c) => a + (c.shooters ?? 0), 0)} label="Class Entries" iconBg="bg-violet-50 text-violet-600" />
        <StatCard icon={<Medal size={20} />} value={grades.length} label="Grades" sub="In the register" iconBg="bg-warning-soft text-warning" />
        <StatCard
          icon={<Layers size={20} />}
          value={classes.filter((c) => c.centreLabel === "X").length}
          label="F-Class Style"
          sub="Centre counts as X"
          iconBg="bg-success-soft text-success"
        />
      </div>

      <BulkBar
        count={selection.count}
        noun="class"
        onClear={selection.clear}
        onDelete={() => setPending(classes.filter((c) => selection.isSelected(c.code)))}
      />

      <div className="mt-3 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <SectionCard title="Rifle Classes" bodyClassName="p-0">
          <div className="table-scroll [--table-offset:324px]">
            <table className="w-full min-w-[820px]">
              <thead>
                <tr className="border-b border-line bg-slate-50">
                  <th className="th w-10">
                    <SelectAllBox
                      checked={selection.allSelected}
                      indeterminate={selection.someSelected}
                      onChange={selection.toggleAll}
                      label="Select all classes"
                    />
                  </th>
                  <th className="th">Class</th>
                  <th className="th">Name</th>
                  <th className="th text-center">Highest Shot Value</th>
                  <th className="th text-center">Centre</th>
                  <th className="th">Also accepted as</th>
                  <th className="th text-right">Entries</th>
                  <th className="th text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {loading && (
                  <tr><td colSpan={8} className="td py-10 text-center"><Loading /></td></tr>
                )}
                {!loading && !classes.length && (
                  <tr>
                    <td colSpan={8} className="td py-12 text-center">
                      <EmptyState title="No classes yet" message="Add the classes your competitions are shot in." />
                    </td>
                  </tr>
                )}
                {classes.map((c) => (
                  <tr
                    key={c.code}
                    className={`transition-colors ${selection.isSelected(c.code) ? "bg-primary-soft/50" : "hover:bg-primary-soft/30"}`}
                  >
                    <td className="td">
                      <RowBox checked={selection.isSelected(c.code)} onChange={() => selection.toggle(c.code)} label={`Select ${c.code}`} />
                    </td>
                    <td className="td">
                      <span className="flex items-center gap-2.5 font-bold text-ink">
                        <Layers size={15} className="text-primary" /> {c.code}
                      </span>
                    </td>
                    <td className="td text-slate-600">{c.name}</td>
                    <td className="td text-center font-semibold">{c.maxPerShot}</td>
                    <td className="td text-center">
                      <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[12px] font-bold text-ink">{c.centreLabel}</span>
                    </td>
                    <td className="td text-[12px] text-muted">{c.aliases?.length ? c.aliases.join(", ") : "—"}</td>
                    <td className="td text-right font-semibold">{c.shooters ?? 0}</td>
                    <td className="td text-right">
                      <span className="inline-flex gap-1.5">
                        <button
                          className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50"
                          aria-label={`Edit ${c.code}`}
                          onClick={() => setEditing(c)}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          className="rounded-lg border border-danger/40 p-2 text-danger transition-colors hover:bg-danger-soft"
                          aria-label={`Remove ${c.code}`}
                          onClick={() => setPending([c])}
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <div className="space-y-4">
          {/*
            Grades are a register now, like the classes. Fullbore uses A/B/C;
            other disciplines grade by name — Master, Expert, Sharpshooter — so a
            fixed list would report every one of those as wrong. Anything not on
            the register is reported instead, with the competitors holding it
            counted so a typed "G" can be found and corrected.
          */}
          <SectionCard
            title="Grades"
            bodyClassName="p-0"
            action={
              <button
                onClick={() => setEditingGrade({ code: "", name: "", n: 99, aliases: [], description: "" } as RifleGrade)}
                className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 text-xs font-semibold text-ink transition-colors hover:bg-slate-50"
              >
                <Plus size={13} /> Add Grade
              </button>
            }
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-line bg-slate-50">
                  <th className="th">Grade</th>
                  <th className="th text-right">Entries</th>
                  <th className="th text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {grades.map((g) => (
                  <tr key={g.code}>
                    <td className="td font-bold">
                      {g.code}
                      {g.name && g.name !== g.code && <span className="ml-1.5 font-normal text-muted">{g.name}</span>}
                    </td>
                    <td className="td text-right">
                      <Link
                        to={`/admin/competitors?grade=${encodeURIComponent(g.code)}`}
                        className="font-semibold text-primary hover:underline"
                      >
                        {g.shooters ?? 0}
                      </Link>
                    </td>
                    <td className="td text-right">
                      <span className="inline-flex gap-1">
                        <button
                          onClick={() => setEditingGrade(g)}
                          className="rounded-lg border border-line p-1 text-muted transition-colors hover:bg-slate-50 hover:text-ink"
                          aria-label={`Edit grade ${g.code}`}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => setPendingGrade(g)}
                          className="rounded-lg border border-line p-1 text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                          aria-label={`Remove grade ${g.code}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
                {unknownGrades.map((u) => (
                  <tr key={`unknown-${u.grade}`} className="bg-warning-soft/40">
                    <td className="td">
                      <span className="font-bold text-ink">{u.grade}</span>
                      <span className="mt-0.5 block text-[11px] font-bold uppercase text-warning">Not in the register</span>
                      {u.suggestion && <span className="block text-[11px] text-muted">did you mean {u.suggestion}?</span>}
                    </td>
                    <td className="td text-right">
                      <Link
                        to={`/admin/competitors?grade=${encodeURIComponent(u.grade)}`}
                        className="font-semibold text-primary hover:underline"
                      >
                        {u.shooters}
                      </Link>
                    </td>
                    <td className="td text-right">
                      <button
                        onClick={() => setEditingGrade({ code: u.grade, name: u.grade, n: 99, aliases: [], description: "" } as RifleGrade)}
                        className="rounded-lg border border-line px-2 py-0.5 text-[11px] font-bold text-primary transition-colors hover:bg-primary-soft"
                      >
                        Add it
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-line px-4 py-2.5 text-[12px] leading-snug text-muted">
              A grade divides a class — an A-grade Target Rifle shooter is not competing against an A-grade F-Class
              shooter. Entry lists are matched against this register, so a value that is not on it is reported rather
              than quietly given a placing board of its own.
              <br />
              To correct a wrong grade, click its count to find the competitors holding it, or fix the workbook and
              upload again.
            </p>
          </SectionCard>

          <div className="card p-4">
            <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink">
              <Info size={15} className="text-primary" /> Why this matters
            </h4>
            <ul className="mt-2 space-y-2 text-[13px] leading-snug text-muted">
              <li>An upload is matched against this list, ignoring spacing and capitalisation.</li>
              <li>Anything unrecognised is reported on the import — a mistyped class can no longer create a phantom one.</li>
              <li>Max&nbsp;/&nbsp;shot and Centre are what the class actually scores, so a new class scores correctly from the start.</li>
              <li>Add a spelling under "also accepted as" when a range records a class differently.</li>
            </ul>
          </div>
        </div>
      </div>

      <ClassEditor
        rifleClass={editing}
        onClose={() => setEditing(null)}
        onSaved={(msg) => {
          setEditing(null);
          setNotice({ tone: "ok", text: msg });
          reload();
        }}
      />

      <ConfirmDialog
        open={pending.length > 0}
        busy={busy}
        title={pending.length === 1 ? "Remove this class?" : `Remove ${pending.length} classes?`}
        intro="This takes the class out of the register."
        items={pending.map((c) => `${c.code} — ${c.name}${c.shooters ? ` (${c.shooters} entries)` : ""}`)}
        note={
          <>
            A class that has been shot cannot be removed — its results would show a class nothing explains. Remove those
            results first, or rename the class if it was recorded wrongly.
          </>
        }
        confirmLabel={pending.length === 1 ? "Remove class" : `Remove ${pending.length} classes`}
        onConfirm={remove}
        onCancel={() => setPending([])}
      />
      {editingGrade && (
        <GradeEditor grade={editingGrade} busy={busy} onCancel={() => setEditingGrade(null)} onSave={saveGrade} />
      )}

      <ConfirmDialog
        open={!!pendingGrade}
        title="Remove this grade?"
        intro={<>It will no longer be accepted on an entry list.</>}
        items={pendingGrade ? [pendingGrade.name && pendingGrade.name !== pendingGrade.code ? `${pendingGrade.code} — ${pendingGrade.name}` : pendingGrade.code] : []}
        note="A grade that competitors still hold cannot be removed — change theirs first. Removing an unused grade means a future entry list carrying it is reported instead of accepted."
        confirmLabel="Remove Grade"
        busy={busy}
        onCancel={() => setPendingGrade(null)}
        onConfirm={removeGrade}
      />
    </div>
  );
}

/* --------------------------------- Editor ---------------------------------- */

function ClassEditor({
  rifleClass,
  onClose,
  onSaved,
}: {
  rifleClass: RifleClass | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [form, setForm] = useState<RifleClass | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (rifleClass && loadedFor !== rifleClass.code) {
    setLoadedFor(rifleClass.code);
    setForm({ ...rifleClass });
    setError(null);
  }
  if (!rifleClass || !form) {
    if (loadedFor !== null && !rifleClass) setLoadedFor(null);
    return null;
  }

  const isNew = !rifleClass.code;
  const set = (k: keyof RifleClass, v: unknown) => setForm((f) => (f ? { ...f, [k]: v } : f));

  const save = async () => {
    setError(null);
    if (!form.code.trim()) return setError("A class needs a code, for example TR.");
    if (!form.name.trim()) return setError("A class needs a name.");
    if (!form.maxPerShot || form.maxPerShot < 1) return setError("The highest value a shot can score must be at least 1.");
    setSaving(true);
    try {
      const body = { ...form, aliases: form.aliases ?? [] };
      const res = isNew
        ? await apiPost<{ message: string }>("/admin/classes", body)
        : await apiPut<{ message: string }>(`/admin/classes/${encodeURIComponent(rifleClass.code)}`, body);
      onSaved(res.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Add rifle class" : `Edit ${rifleClass.code}`}>
      <div className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-[13px] font-semibold text-danger">
            <AlertCircle size={15} className="mt-px shrink-0" /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Class code</label>
            <input className="input" placeholder="TR" value={form.code} onChange={(e) => set("code", e.target.value)} />
            <p className="mt-1 text-xs text-muted">What the spreadsheet's Class column says.</p>
          </div>
          <div>
            <label className="label">Full name</label>
            <input className="input" placeholder="Target Rifle" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Highest value a shot can score</label>
            <input
              type="number"
              className="input"
              value={form.maxPerShot}
              onChange={(e) => set("maxPerShot", Number(e.target.value))}
            />
            <p className="mt-1 text-xs text-muted">TR is 5, F-Class 6, an ISSF target 10.</p>
          </div>
          <div>
            <label className="label">Centre is called</label>
            <input className="input" placeholder="V" value={form.centreLabel} onChange={(e) => set("centreLabel", e.target.value.toUpperCase())} />
            <p className="mt-1 text-xs text-muted">V for Target Rifle, X for F-Class.</p>
          </div>
        </div>

        <div>
          <label className="label">Also accepted as</label>
          <input
            className="input"
            placeholder="targetrifle, target"
            value={(form.aliases ?? []).join(", ")}
            onChange={(e) => set("aliases", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          />
          <p className="mt-1 text-xs text-muted">
            Other spellings an upload may use, separated by commas. Spacing and capitalisation are already ignored.
          </p>
        </div>

        <div>
          <label className="label">Description</label>
          <input
            className="input"
            placeholder="Target Rifle — 5 to 1 with a V centre"
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save class
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/*
 * A grade record. The code is what the entry list carries; aliases catch the
 * spellings a workbook might use for the same thing ("A Grade" for "A"), so a
 * known grade is not reported as unknown over punctuation.
 */
function GradeEditor({
  grade,
  busy,
  onCancel,
  onSave,
}: {
  grade: RifleGrade;
  busy: boolean;
  onCancel: () => void;
  onSave: (g: RifleGrade) => void;
}) {
  const [code, setCode] = useState(grade.code);
  const [name, setName] = useState(grade.name || grade.code);
  const [n, setN] = useState(grade.n ?? 99);
  const [aliases, setAliases] = useState((grade.aliases || []).join(", "));

  return (
    <Modal
      open
      title={grade.code ? `Edit grade ${grade.code}` : "Add Grade"}
      onClose={onCancel}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            disabled={busy || !code.trim()}
            onClick={() =>
              onSave({
                ...grade,
                code: code.trim(),
                name: name.trim() || code.trim(),
                n: Number(n) || 99,
                aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
              })
            }
          >
            {busy ? "Saving..." : "Save Grade"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Code <span className="text-danger">*</span></label>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="A, or Master" />
            <p className="mt-1 text-[12px] text-muted">Exactly as the entry list writes it.</p>
          </div>
          <div>
            <label className="label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="A Grade" />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Order</label>
            <input
              type="number"
              className="input"
              value={n}
              onChange={(e) => setN(Number(e.target.value))}
            />
            <p className="mt-1 text-[12px] text-muted">Where it sits when boards are printed — A before B before C.</p>
          </div>
          <div>
            <label className="label">Also accepted as</label>
            <input
              className="input"
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="a grade, grade a"
            />
            <p className="mt-1 text-[12px] text-muted">Comma separated. Spacing and capitalisation are ignored.</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
